// src/data/auditLogs.ts
// --------------------
// Repository for the `audit_logs` table — the app-wide accountability log. Owns
// the write (insert, used by logAudit), the paginated/filterable admin viewer
// read, and the failed-notification read. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type AuditLogRow = Database["public"]["Tables"]["audit_logs"]["Row"];
type AuditLogInsert = Database["public"]["Tables"]["audit_logs"]["Insert"];

// One actor's involvement in a request, from get_request_participants (db-61).
export type RequestParticipant = {
  user_id: string;
  display_name: string | null;
  role: string | null;
  first_touch: string | null;
  last_touch: string | null;
  action_count: number;
  actions: string[] | null;
};
export type FailedNotificationRow = {
  id: number;
  created_at: string | null;
  details: string | null;
};

// Append an audit entry. The audit_logs RLS WITH CHECK ties user_id to auth.uid()
// server-side, so the client only sends action + details.
export async function insertAuditLog(
  action: string,
  details: string,
  requestId?: number | null
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  // request_id (db-61) is cast-tolerated until `npm run gen:types` regenerates the
  // Insert type to include it; harmless once it does.
  const row = { action, details, request_id: requestId ?? null } as AuditLogInsert;
  const { error } = await c.from("audit_logs").insert(row);
  if (error) logger.warn("Audit log failed:", error.message);
  return { error };
}

// Record a FAILED sign-in (wrong password / bad access code) via the db-175
// SECURITY DEFINER RPC. These attempts have no session, so the normal audit_logs
// INSERT policy (user_id = auth.uid()) can never accept them — the RPC is the only
// route. It only ever writes 'login_failed' / 'access_code_failed' (a hard-coded
// allowlist in the function), so callers can't repurpose it. Fire-and-forget:
// never blocks or breaks the login flow.
export async function logAuthFailure(action: string, details: string): Promise<void> {
  const c = getTypedClient();
  if (!c) return;
  // log_auth_failure (db-175) isn't in the generated types until `npm run gen:types`
  // runs post-migration; cast this one call so typecheck stays green regardless of
  // ordering (same pattern as get_request_participants below).
  const client = c as unknown as {
    rpc: (
      name: string,
      args: Record<string, unknown>
    ) => Promise<{ error: { message: string } | null }>;
  };
  const { error } = await client.rpc("log_auth_failure", {
    p_action: action,
    p_details: details,
  });
  if (error) logger.warn("logAuthFailure failed:", error.message);
}

// Per-person "who touched this request" summary via the db-61 SECURITY DEFINER
// RPC (internal-only; a shelter user gets an empty list). null on error / no client.
export async function getRequestParticipants(
  requestId: number | string
): Promise<RequestParticipant[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  // get_request_participants (db-61) isn't in the generated types until
  // `npm run gen:types` runs post-migration; cast this one call so typecheck is
  // green regardless of ordering. The return is validated by the cast below.
  const client = c as unknown as {
    rpc: (
      name: string,
      args: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
  };
  const { data, error } = await client.rpc("get_request_participants", {
    p_request_id: requestId as number,
  });
  if (error) {
    logger.warn("getRequestParticipants failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as RequestParticipant[];
}

// Paginated, newest-first audit log for the admin viewer. `actionIn` (a group of
// actions) takes precedence over `actionEq` (a single action); omit both for all.
export async function listAuditLogs(opts: {
  page: number;
  pageSize: number;
  actionIn?: string[] | null;
  actionEq?: string | null;
  search?: string | null;
}): Promise<AuditLogRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  let q = c.from("audit_logs").select("*");
  if (opts.actionIn) q = q.in("action", opts.actionIn);
  else if (opts.actionEq) q = q.eq("action", opts.actionEq);
  // Free-text search: match the details blob OR the action name. `%` and `,` are
  // escaped so a search term can't break out of the ilike pattern / or() filter.
  const term = (opts.search || "").trim();
  if (term) {
    const safe = term.replace(/[%,()]/g, " ");
    const orParts = [`details.ilike.%${safe}%`, `action.ilike.%${safe}%`];
    // Request-id search. The details column is stored as JSON ({"request_id":680,…})
    // but the viewer PRETTY-PRINTS it as "request_id: 680", so an ilike on the
    // displayed form misses (the JSON has no space after the colon). Match the
    // first-class, indexed request_id column (db-61) instead, for both a bare number
    // ("680") and the copy-pasted display form ("request_id: 680" / "request id 680").
    let rid: string | null = null;
    if (/^\d+$/.test(term)) rid = term;
    else {
      const m = /request[\s_]*id[\s:#]*(\d+)/i.exec(term);
      if (m) rid = m[1];
    }
    // Guard against an id too large for a bigint (keeps the filter well-formed).
    if (rid && Number.isSafeInteger(Number(rid))) orParts.push(`request_id.eq.${rid}`);
    q = q.or(orParts.join(","));
  }
  const { data, error } = await q
    .order("created_at", { ascending: false })
    .range(opts.page * opts.pageSize, (opts.page + 1) * opts.pageSize - 1);
  if (error) {
    logger.warn("listAuditLogs failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as AuditLogRow[];
}

// The most recent failed-notification audit entries (admin diagnostics panel).
export async function listFailedNotifications(): Promise<FailedNotificationRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("audit_logs")
    .select("id, created_at, details")
    .eq("action", "email_send_failed")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) {
    logger.warn("listFailedNotifications failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as FailedNotificationRow[];
}
